import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Building,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Factory,
  FileText,
  Package,
  Plus,
  Rocket,
  Settings,
  Target,
  TrendingUp,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SectionHeader({ number, title, icon: Icon, expanded, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all"
    >
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
          {number}
        </span>
        <Icon className="w-5 h-5" />
        <span className="font-semibold">{title}</span>
      </div>
      {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );
}

// Create SDP Modal Component with Full Form
function CreateSDPModal({ open, onClose, onSuccess, linhasProduto, users, token }) {
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    1: true, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false, 8: false, 9: false, 10: false
  });

  const [formData, setFormData] = useState({
    // Section 1 - Informações Gerais
    nome_projeto: "",
    cliente: "",
    origem_demanda: "",
    area_responsavel: "",
    prazo_desejado: "",
    assignee_type: "",
    assignee_id: "",
    
    // Section 2 - Definição do Problema
    problema_resolve: "",
    quem_sofre_problema: "",
    solucao_atual: "",
    por_que_insuficiente: "",
    urgencia_comercial: "",
    
    // Section 3 - Perfil de Mercado
    segmento_mercado: "",
    segmento_outro: "",
    mercado_alvo: "",
    volume_anual_potencial: "",
    ticket_medio_estimado: "",
    concorrente_direto: "",
    
    // Section 4 - Proposta de Valor
    diferencial_principal: "",
    frase_venda: "",
    
    // Section 5 - Especificação Técnica
    estrutura_metalica: false,
    eletronica_embarcada: false,
    software_proprio: false,
    integracao_modulo_terceiro: false,
    componentes_externos: "",
    componentes_tabela: [],
    requisitos_normas: "",
    ambiente_operacao: "",
    vida_util_anos: "",
    sla_manutencao: "",
    // Medidas máximas
    medida_largura: "",
    medida_profundidade: "",
    medida_altura: "",
    peso_maximo: "",
    
    // Section 6 - Make or Buy
    fabricado_interno: "",
    comprado_terceiros: "",
    dependencia_fornecedor: "",
    material_consignado: "",
    
    // Section 7 - Impacto Industrial
    fabrica_suporta: "",
    novo_ferramental: false,
    nova_celula_produtiva: false,
    treinamento_especifico: false,
    ampliacao_estoque: false,
    complexidade_produtiva: "",
    tempo_montagem_unidade: "",
    
    // Section 8 - Análise Financeira
    custo_estimado_unitario: "",
    preco_alvo_venda: "",
    margem_bruta_estimada: "",
    investimento_inicial: "",
    break_even_unidades: "",
    
    // Section 9 - Riscos
    riscos_tecnicos: "",
    riscos_comerciais: "",
    riscos_operacionais: "",
    nivel_risco_geral: "",
    
    // Section 10 - Estratégia de Lançamento
    tipo_lancamento: "",
    necessita_piloto: "",
    cliente_ancora: "",
    contrato_intencao: "",
    
    // Legacy fields for backend compatibility
    prioridade: "media",
    tipo: "novo_produto",
    justificativa: "",
    responsavel_tecnico_id: "",
    linha_produto_id: ""
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build justificativa from form sections
    const justificativa = `
## PROBLEMA/OPORTUNIDADE
${formData.problema_resolve || 'Não informado'}

## QUEM SOFRE O PROBLEMA
${formData.quem_sofre_problema || 'Não informado'}

## SOLUÇÃO ATUAL
${formData.solucao_atual || 'Não informado'}

## PROPOSTA DE VALOR
Diferencial: ${formData.diferencial_principal || 'Não informado'}
${formData.frase_venda || ''}

## ANÁLISE DE MERCADO
Segmento: ${formData.segmento_mercado || 'Não informado'}
Mercado Alvo: ${formData.mercado_alvo || 'Não informado'}
Volume Potencial: ${formData.volume_anual_potencial || 'Não informado'}
Ticket Médio: ${formData.ticket_medio_estimado || 'Não informado'}
Concorrência: ${formData.concorrente_direto || 'Não informado'}

## ESPECIFICAÇÃO TÉCNICA
Componentes: ${[
  formData.estrutura_metalica && 'Estrutura metálica',
  formData.eletronica_embarcada && 'Eletrônica embarcada',
  formData.software_proprio && 'Software próprio',
  formData.integracao_modulo_terceiro && 'Integração com módulo terceiro'
].filter(Boolean).join(', ') || 'Não especificado'}
Ambiente: ${formData.ambiente_operacao || 'Não informado'}
Vida útil: ${formData.vida_util_anos || 'Não informado'} anos

### LISTA DE COMPONENTES
${(formData.componentes_tabela || []).length > 0 ? 
  formData.componentes_tabela.map((c, i) => `${i+1}. ${c.tipo || '-'} | ${c.fabricante || '-'} | ${c.modelo || '-'} | R$ ${(c.custo || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}`).join('\n') 
  : 'Nenhum componente listado'}
**Custo Total Componentes: R$ ${(formData.componentes_tabela || []).reduce((sum, c) => sum + (parseFloat(c.custo) || 0), 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}**

### MEDIDAS MÁXIMAS
Largura: ${formData.medida_largura || 'N/I'} mm | Profundidade: ${formData.medida_profundidade || 'N/I'} mm | Altura: ${formData.medida_altura || 'N/I'} mm
Peso Máximo: ${formData.peso_maximo || 'N/I'} kg

## MAKE OR BUY
Fabricado interno: ${formData.fabricado_interno || 'Não informado'}
Comprado terceiros: ${formData.comprado_terceiros || 'Não informado'}

## IMPACTO INDUSTRIAL
Fábrica suporta: ${formData.fabrica_suporta || 'Não informado'}
Complexidade: ${formData.complexidade_produtiva || 'Não informado'}/5
Tempo montagem: ${formData.tempo_montagem_unidade || 'Não informado'}

## ANÁLISE FINANCEIRA
Custo unitário: ${formData.custo_estimado_unitario || 'Não informado'}
Preço venda: ${formData.preco_alvo_venda || 'Não informado'}
Margem bruta: ${formData.margem_bruta_estimada || 'Não informado'}
Investimento inicial: ${formData.investimento_inicial || 'Não informado'}
Break-even: ${formData.break_even_unidades || 'Não informado'} un

## RISCOS
Técnicos: ${formData.riscos_tecnicos || 'Não informado'}
Comerciais: ${formData.riscos_comerciais || 'Não informado'}
Operacionais: ${formData.riscos_operacionais || 'Não informado'}
Nível geral: ${formData.nivel_risco_geral || 'Não informado'}

## ESTRATÉGIA DE LANÇAMENTO
Tipo: ${formData.tipo_lancamento || 'Não informado'}
Piloto: ${formData.necessita_piloto || 'Não informado'}
Cliente âncora: ${formData.cliente_ancora || 'Não informado'}
`.trim();

    // Determine tipo based on origem_demanda
    let tipo = 'novo_produto';
    if (formData.origem_demanda === 'evolucao_existente') {
      tipo = 'melhoria_tecnica';
    } else if (formData.origem_demanda === 'demanda_cliente') {
      tipo = 'customizacao';
    }

    // Use prioridade selected by user
    const prioridade = formData.prioridade || 'media';

    const payload = {
      nome_projeto: formData.nome_projeto,
      cliente: formData.cliente,
      linha_produto_id: formData.linha_produto_id || null,
      tipo: tipo,
      prioridade: prioridade,
      justificativa: justificativa,
      prazo_desejado: formData.prazo_desejado || null,
      responsavel_tecnico_id: formData.responsavel_tecnico_id || null,
      assignee_type: formData.assignee_type || null,
      assignee_id: formData.assignee_type === 'user' ? formData.assignee_id : null,
      dados_completos: formData // Store all form data for future reference
    };

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/sdp`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("SDP criada com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar SDP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Rocket className="w-6 h-6 text-blue-600" />
            Nova Solicitação de Desenvolvimento de Produto
          </DialogTitle>
          <DialogDescription>
            Preencha o formulário completo para uma análise detalhada
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2 space-y-3">
          {/* Section 1 - Informações Gerais */}
          <div>
            <SectionHeader 
              number="1" 
              title="INFORMAÇÕES GERAIS" 
              icon={FileText}
              expanded={expandedSections[1]}
              onToggle={() => toggleSection(1)}
            />
            {expandedSections[1] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Nome provisório do produto *</Label>
                  <Input
                    value={formData.nome_projeto}
                    onChange={(e) => updateField('nome_projeto', e.target.value)}
                    required
                    placeholder="Ex: Smart Safe 3.0"
                  />
                </div>
                <div>
                  <Label>Cliente solicitante (se houver)</Label>
                  <Input
                    value={formData.cliente}
                    onChange={(e) => updateField('cliente', e.target.value)}
                    placeholder="Nome do cliente"
                  />
                </div>
                <div>
                  <Label>É:</Label>
                  <RadioGroup 
                    value={formData.origem_demanda} 
                    onValueChange={(v) => updateField('origem_demanda', v)}
                    className="mt-2 space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="demanda_cliente" id="demanda_cliente" />
                      <Label htmlFor="demanda_cliente" className="font-normal">Demanda específica de cliente</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="iniciativa_interna" id="iniciativa_interna" />
                      <Label htmlFor="iniciativa_interna" className="font-normal">Iniciativa estratégica interna</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="evolucao_existente" id="evolucao_existente" />
                      <Label htmlFor="evolucao_existente" className="font-normal">Evolução de produto existente</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="produto_novo" id="produto_novo" />
                      <Label htmlFor="produto_novo" className="font-normal">Produto totalmente novo</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Área responsável pela solicitação</Label>
                    <Input
                      value={formData.area_responsavel}
                      onChange={(e) => updateField('area_responsavel', e.target.value)}
                      placeholder="Ex: Comercial, Engenharia..."
                    />
                  </div>
                  <div>
                    <Label>Data desejada de lançamento</Label>
                    <Input
                      type="date"
                      value={formData.prazo_desejado}
                      onChange={(e) => updateField('prazo_desejado', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Prioridade da SDP */}
                <div className="border-t pt-4 mt-4">
                  <Label className="text-base font-semibold mb-3 block">Prioridade:</Label>
                  <RadioGroup 
                    value={formData.prioridade} 
                    onValueChange={(v) => updateField('prioridade', v)}
                    className="grid grid-cols-4 gap-3"
                  >
                    <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${formData.prioridade === 'baixa' ? 'border-green-500 bg-green-50' : ''}`}>
                      <RadioGroupItem value="baixa" id="prioridade_baixa" className="sr-only" />
                      <Label htmlFor="prioridade_baixa" className="cursor-pointer text-center">
                        <span className="w-3 h-3 rounded-full bg-green-500 inline-block mb-1"></span>
                        <span className="block font-medium">Baixa</span>
                      </Label>
                    </div>
                    <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${formData.prioridade === 'media' ? 'border-yellow-500 bg-yellow-50' : ''}`}>
                      <RadioGroupItem value="media" id="prioridade_media" className="sr-only" />
                      <Label htmlFor="prioridade_media" className="cursor-pointer text-center">
                        <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block mb-1"></span>
                        <span className="block font-medium">Média</span>
                      </Label>
                    </div>
                    <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${formData.prioridade === 'alta' ? 'border-orange-500 bg-orange-50' : ''}`}>
                      <RadioGroupItem value="alta" id="prioridade_alta" className="sr-only" />
                      <Label htmlFor="prioridade_alta" className="cursor-pointer text-center">
                        <span className="w-3 h-3 rounded-full bg-orange-500 inline-block mb-1"></span>
                        <span className="block font-medium">Alta</span>
                      </Label>
                    </div>
                    <div className={`flex flex-col items-center p-3 border rounded-lg hover:bg-slate-100 transition-colors cursor-pointer ${formData.prioridade === 'estrategica' ? 'border-red-500 bg-red-50' : ''}`}>
                      <RadioGroupItem value="estrategica" id="prioridade_estrategica" className="sr-only" />
                      <Label htmlFor="prioridade_estrategica" className="cursor-pointer text-center">
                        <span className="w-3 h-3 rounded-full bg-red-500 inline-block mb-1"></span>
                        <span className="block font-medium">Estratégica</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {/* Atribuição da SDP */}
                <div className="border-t pt-4 mt-4">
                  <Label className="text-base font-semibold mb-3 block">Atribuir SDP a:</Label>
                  <RadioGroup 
                    value={formData.assignee_type} 
                    onValueChange={(v) => {
                      updateField('assignee_type', v);
                      if (v !== 'user') {
                        updateField('assignee_id', '');
                      }
                    }}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors">
                      <RadioGroupItem value="admin" id="assign_admin" />
                      <Label htmlFor="assign_admin" className="font-normal cursor-pointer flex-1">
                        <span className="font-medium">Grupo Admin</span>
                        <span className="text-slate-500 text-sm block">Atribuir à equipe de administração</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors">
                      <RadioGroupItem value="engenharia" id="assign_engenharia" />
                      <Label htmlFor="assign_engenharia" className="font-normal cursor-pointer flex-1">
                        <span className="font-medium">Grupo Engenharia</span>
                        <span className="text-slate-500 text-sm block">Atribuir à equipe de engenharia</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-100 transition-colors">
                      <RadioGroupItem value="user" id="assign_user" />
                      <Label htmlFor="assign_user" className="font-normal cursor-pointer flex-1">
                        <span className="font-medium">Usuário Específico</span>
                        <span className="text-slate-500 text-sm block">Atribuir a um usuário individual</span>
                      </Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.assignee_type === 'user' && (
                    <div className="mt-3 pl-6">
                      <Label>Selecione o usuário:</Label>
                      <Select 
                        value={formData.assignee_id || "placeholder"} 
                        onValueChange={(v) => updateField('assignee_id', v === "placeholder" ? "" : v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione um usuário..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placeholder" disabled>Selecione um usuário...</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 2 - Definição do Problema */}
          <div>
            <SectionHeader 
              number="2" 
              title="DEFINIÇÃO DO PROBLEMA / OPORTUNIDADE" 
              icon={Target}
              expanded={expandedSections[2]}
              onToggle={() => toggleSection(2)}
            />
            {expandedSections[2] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Qual problema este produto resolve?</Label>
                  <Textarea
                    value={formData.problema_resolve}
                    onChange={(e) => updateField('problema_resolve', e.target.value)}
                    rows={3}
                    placeholder="Descreva o problema principal..."
                  />
                </div>
                <div>
                  <Label>Quem sofre esse problema hoje?</Label>
                  <Input
                    value={formData.quem_sofre_problema}
                    onChange={(e) => updateField('quem_sofre_problema', e.target.value)}
                    placeholder="Ex: Bancos, varejo, transportadoras..."
                  />
                </div>
                <div>
                  <Label>Como o cliente resolve isso atualmente?</Label>
                  <Textarea
                    value={formData.solucao_atual}
                    onChange={(e) => updateField('solucao_atual', e.target.value)}
                    rows={2}
                    placeholder="Solução atual utilizada..."
                  />
                </div>
                <div>
                  <Label>Por que essa solução atual é insuficiente?</Label>
                  <Textarea
                    value={formData.por_que_insuficiente}
                    onChange={(e) => updateField('por_que_insuficiente', e.target.value)}
                    rows={2}
                    placeholder="Limitações da solução atual..."
                  />
                </div>
                <div>
                  <Label>Existe urgência comercial? Qual o motivo?</Label>
                  <Input
                    value={formData.urgencia_comercial}
                    onChange={(e) => updateField('urgencia_comercial', e.target.value)}
                    placeholder="Sim/Não - motivo..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3 - Perfil de Mercado */}
          <div>
            <SectionHeader 
              number="3" 
              title="PERFIL DE MERCADO" 
              icon={Building}
              expanded={expandedSections[3]}
              onToggle={() => toggleSection(3)}
            />
            {expandedSections[3] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Segmento de mercado:</Label>
                  <RadioGroup 
                    value={formData.segmento_mercado} 
                    onValueChange={(v) => updateField('segmento_mercado', v)}
                    className="mt-2 grid grid-cols-2 gap-2"
                  >
                    {['Bancário', 'Transporte de valores', 'Varejo', 'Loterias / VLT', 'Industrial', 'Outro'].map(seg => (
                      <div key={seg} className="flex items-center space-x-2">
                        <RadioGroupItem value={seg.toLowerCase().replace(/ /g, '_')} id={`seg_${seg}`} />
                        <Label htmlFor={`seg_${seg}`} className="font-normal">{seg}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {formData.segmento_mercado === 'outro' && (
                    <Input
                      className="mt-2"
                      value={formData.segmento_outro}
                      onChange={(e) => updateField('segmento_outro', e.target.value)}
                      placeholder="Especifique..."
                    />
                  )}
                </div>
                <div>
                  <Label>Mercado alvo:</Label>
                  <RadioGroup 
                    value={formData.mercado_alvo} 
                    onValueChange={(v) => updateField('mercado_alvo', v)}
                    className="mt-2 flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="brasil" id="mercado_brasil" />
                      <Label htmlFor="mercado_brasil" className="font-normal">Brasil</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="america_latina" id="mercado_latam" />
                      <Label htmlFor="mercado_latam" className="font-normal">América Latina</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="global" id="mercado_global" />
                      <Label htmlFor="mercado_global" className="font-normal">Global</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Estimativa de volume anual potencial</Label>
                    <Input
                      value={formData.volume_anual_potencial}
                      onChange={(e) => updateField('volume_anual_potencial', e.target.value)}
                      placeholder="Ex: 500 unidades"
                    />
                  </div>
                  <div>
                    <Label>Ticket médio estimado</Label>
                    <Input
                      value={formData.ticket_medio_estimado}
                      onChange={(e) => updateField('ticket_medio_estimado', e.target.value)}
                      placeholder="Ex: R$ 50.000"
                    />
                  </div>
                </div>
                <div>
                  <Label>Existe concorrente direto? Quem?</Label>
                  <Input
                    value={formData.concorrente_direto}
                    onChange={(e) => updateField('concorrente_direto', e.target.value)}
                    placeholder="Nome dos concorrentes..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4 - Proposta de Valor */}
          <div>
            <SectionHeader 
              number="4" 
              title="PROPOSTA DE VALOR" 
              icon={TrendingUp}
              expanded={expandedSections[4]}
              onToggle={() => toggleSection(4)}
            />
            {expandedSections[4] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Qual será o principal diferencial?</Label>
                  <RadioGroup 
                    value={formData.diferencial_principal} 
                    onValueChange={(v) => updateField('diferencial_principal', v)}
                    className="mt-2 grid grid-cols-3 gap-2"
                  >
                    {['Preço', 'Robustez', 'Tecnologia', 'Integração', 'Segurança', 'Customização'].map(dif => (
                      <div key={dif} className="flex items-center space-x-2">
                        <RadioGroupItem value={dif.toLowerCase()} id={`dif_${dif}`} />
                        <Label htmlFor={`dif_${dif}`} className="font-normal">{dif}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label>Em uma frase: por que o cliente compraria este produto?</Label>
                  <Textarea
                    value={formData.frase_venda}
                    onChange={(e) => updateField('frase_venda', e.target.value)}
                    rows={2}
                    placeholder="Proposta de valor em uma frase..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 5 - Especificação Técnica */}
          <div>
            <SectionHeader 
              number="5" 
              title="ESPECIFICAÇÃO TÉCNICA PRELIMINAR" 
              icon={Settings}
              expanded={expandedSections[5]}
              onToggle={() => toggleSection(5)}
            />
            {expandedSections[5] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>O produto terá:</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="estrutura_metalica"
                        checked={formData.estrutura_metalica}
                        onCheckedChange={(c) => updateField('estrutura_metalica', c)}
                      />
                      <Label htmlFor="estrutura_metalica" className="font-normal">Estrutura metálica</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="eletronica_embarcada"
                        checked={formData.eletronica_embarcada}
                        onCheckedChange={(c) => updateField('eletronica_embarcada', c)}
                      />
                      <Label htmlFor="eletronica_embarcada" className="font-normal">Eletrônica embarcada</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="software_proprio"
                        checked={formData.software_proprio}
                        onCheckedChange={(c) => updateField('software_proprio', c)}
                      />
                      <Label htmlFor="software_proprio" className="font-normal">Software próprio</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="integracao_modulo_terceiro"
                        checked={formData.integracao_modulo_terceiro}
                        onCheckedChange={(c) => updateField('integracao_modulo_terceiro', c)}
                      />
                      <Label htmlFor="integracao_modulo_terceiro" className="font-normal">Integração com módulo terceiro (ex: reciclador, validador, PC)</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-base font-semibold mb-2 block">Componentes do Produto</Label>
                  <p className="text-sm text-slate-500 mb-3">Adicione os componentes principais com suas especificações e custos estimados.</p>
                  
                  {/* Tabela de Componentes */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Tipo</th>
                          <th className="px-3 py-2 text-left font-medium">Fabricante</th>
                          <th className="px-3 py-2 text-left font-medium">Modelo</th>
                          <th className="px-3 py-2 text-right font-medium">Custo Est. (R$)</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(formData.componentes_tabela || []).map((comp, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2">
                              <Input
                                value={comp.tipo || ""}
                                onChange={(e) => {
                                  const newComps = [...formData.componentes_tabela];
                                  newComps[idx] = { ...newComps[idx], tipo: e.target.value };
                                  updateField('componentes_tabela', newComps);
                                }}
                                placeholder="Ex: Sensor"
                                className="h-8"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                value={comp.fabricante || ""}
                                onChange={(e) => {
                                  const newComps = [...formData.componentes_tabela];
                                  newComps[idx] = { ...newComps[idx], fabricante: e.target.value };
                                  updateField('componentes_tabela', newComps);
                                }}
                                placeholder="Ex: Bosch"
                                className="h-8"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                value={comp.modelo || ""}
                                onChange={(e) => {
                                  const newComps = [...formData.componentes_tabela];
                                  newComps[idx] = { ...newComps[idx], modelo: e.target.value };
                                  updateField('componentes_tabela', newComps);
                                }}
                                placeholder="Ex: XYZ-100"
                                className="h-8"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                value={comp.custo || ""}
                                onChange={(e) => {
                                  const newComps = [...formData.componentes_tabela];
                                  newComps[idx] = { ...newComps[idx], custo: parseFloat(e.target.value) || 0 };
                                  updateField('componentes_tabela', newComps);
                                }}
                                placeholder="0,00"
                                className="h-8 text-right"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  const newComps = formData.componentes_tabela.filter((_, i) => i !== idx);
                                  updateField('componentes_tabela', newComps);
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {(!formData.componentes_tabela || formData.componentes_tabela.length === 0) && (
                          <tr className="border-t">
                            <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                              Nenhum componente adicionado
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const newComps = [...(formData.componentes_tabela || []), { tipo: "", fabricante: "", modelo: "", custo: 0 }];
                      updateField('componentes_tabela', newComps);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Componente
                  </Button>
                  
                  {/* Total Estimado */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
                    <span className="font-semibold text-blue-800">Custo Total Estimado dos Componentes:</span>
                    <span className="text-xl font-bold text-blue-900">
                      R$ {((formData.componentes_tabela || []).reduce((sum, comp) => sum + (parseFloat(comp.custo) || 0), 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                
                {/* Medidas Máximas */}
                <div className="border-t pt-4 mt-4">
                  <Label className="text-base font-semibold mb-3 block">Medidas Máximas do Produto</Label>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Largura (mm)</Label>
                      <Input
                        type="number"
                        value={formData.medida_largura}
                        onChange={(e) => updateField('medida_largura', e.target.value)}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Profundidade (mm)</Label>
                      <Input
                        type="number"
                        value={formData.medida_profundidade}
                        onChange={(e) => updateField('medida_profundidade', e.target.value)}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Altura (mm)</Label>
                      <Input
                        type="number"
                        value={formData.medida_altura}
                        onChange={(e) => updateField('medida_altura', e.target.value)}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Peso Máximo (kg)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.peso_maximo}
                        onChange={(e) => updateField('peso_maximo', e.target.value)}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>Requisitos obrigatórios (normas, certificações, homologações)</Label>
                  <Input
                    value={formData.requisitos_normas}
                    onChange={(e) => updateField('requisitos_normas', e.target.value)}
                    placeholder="Ex: ISO 9001, ABNT..."
                  />
                </div>
                <div>
                  <Label>Ambiente de operação:</Label>
                  <RadioGroup 
                    value={formData.ambiente_operacao} 
                    onValueChange={(v) => updateField('ambiente_operacao', v)}
                    className="mt-2 flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="interno" id="amb_interno" />
                      <Label htmlFor="amb_interno" className="font-normal">Interno</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="externo" id="amb_externo" />
                      <Label htmlFor="amb_externo" className="font-normal">Externo</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="alto_risco" id="amb_risco" />
                      <Label htmlFor="amb_risco" className="font-normal">Alto risco (segurança)</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vida útil esperada (anos)</Label>
                    <Input
                      value={formData.vida_util_anos}
                      onChange={(e) => updateField('vida_util_anos', e.target.value)}
                      placeholder="Ex: 10"
                    />
                  </div>
                  <div>
                    <Label>SLA de manutenção esperado</Label>
                    <Input
                      value={formData.sla_manutencao}
                      onChange={(e) => updateField('sla_manutencao', e.target.value)}
                      placeholder="Ex: 24h, 48h..."
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 6 - Make or Buy */}
          <div>
            <SectionHeader 
              number="6" 
              title="MAKE OR BUY (FABRICAR OU INTEGRAR)" 
              icon={Package}
              expanded={expandedSections[6]}
              onToggle={() => toggleSection(6)}
            />
            {expandedSections[6] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>O que será fabricado internamente?</Label>
                  <Textarea
                    value={formData.fabricado_interno}
                    onChange={(e) => updateField('fabricado_interno', e.target.value)}
                    rows={2}
                    placeholder="Liste os componentes fabricados internamente..."
                  />
                </div>
                <div>
                  <Label>O que será comprado de terceiros?</Label>
                  <Textarea
                    value={formData.comprado_terceiros}
                    onChange={(e) => updateField('comprado_terceiros', e.target.value)}
                    rows={2}
                    placeholder="Liste os componentes comprados..."
                  />
                </div>
                <div>
                  <Label>Existe dependência crítica de fornecedor?</Label>
                  <Input
                    value={formData.dependencia_fornecedor}
                    onChange={(e) => updateField('dependencia_fornecedor', e.target.value)}
                    placeholder="Descreva dependências..."
                  />
                </div>
                <div>
                  <Label>Haverá material consignado?</Label>
                  <Input
                    value={formData.material_consignado}
                    onChange={(e) => updateField('material_consignado', e.target.value)}
                    placeholder="Sim/Não - detalhes..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 7 - Impacto Industrial */}
          <div>
            <SectionHeader 
              number="7" 
              title="IMPACTO INDUSTRIAL" 
              icon={Factory}
              expanded={expandedSections[7]}
              onToggle={() => toggleSection(7)}
            />
            {expandedSections[7] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>A fábrica atual suporta esse produto?</Label>
                  <RadioGroup 
                    value={formData.fabrica_suporta} 
                    onValueChange={(v) => updateField('fabrica_suporta', v)}
                    className="mt-2 flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="sim" id="fab_sim" />
                      <Label htmlFor="fab_sim" className="font-normal">Sim</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="com_adaptacoes" id="fab_adapt" />
                      <Label htmlFor="fab_adapt" className="font-normal">Com adaptações</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="exigira_investimento" id="fab_invest" />
                      <Label htmlFor="fab_invest" className="font-normal">Exigirá investimento</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <Label>Será necessário:</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="novo_ferramental"
                        checked={formData.novo_ferramental}
                        onCheckedChange={(c) => updateField('novo_ferramental', c)}
                      />
                      <Label htmlFor="novo_ferramental" className="font-normal">Novo ferramental</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="nova_celula_produtiva"
                        checked={formData.nova_celula_produtiva}
                        onCheckedChange={(c) => updateField('nova_celula_produtiva', c)}
                      />
                      <Label htmlFor="nova_celula_produtiva" className="font-normal">Nova célula produtiva</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="treinamento_especifico"
                        checked={formData.treinamento_especifico}
                        onCheckedChange={(c) => updateField('treinamento_especifico', c)}
                      />
                      <Label htmlFor="treinamento_especifico" className="font-normal">Treinamento específico</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="ampliacao_estoque"
                        checked={formData.ampliacao_estoque}
                        onCheckedChange={(c) => updateField('ampliacao_estoque', c)}
                      />
                      <Label htmlFor="ampliacao_estoque" className="font-normal">Ampliação de estoque</Label>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Complexidade produtiva (1 a 5)</Label>
                    <Select value={formData.complexidade_produtiva || "placeholder"} onValueChange={(v) => updateField('complexidade_produtiva', v === "placeholder" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder" disabled>Selecione...</SelectItem>
                        <SelectItem value="1">1 - Muito baixa</SelectItem>
                        <SelectItem value="2">2 - Baixa</SelectItem>
                        <SelectItem value="3">3 - Média</SelectItem>
                        <SelectItem value="4">4 - Alta</SelectItem>
                        <SelectItem value="5">5 - Muito alta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tempo estimado de montagem por unidade</Label>
                    <Input
                      value={formData.tempo_montagem_unidade}
                      onChange={(e) => updateField('tempo_montagem_unidade', e.target.value)}
                      placeholder="Ex: 8 horas"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 8 - Análise Financeira */}
          <div>
            <SectionHeader 
              number="8" 
              title="ANÁLISE FINANCEIRA PRELIMINAR" 
              icon={DollarSign}
              expanded={expandedSections[8]}
              onToggle={() => toggleSection(8)}
            />
            {expandedSections[8] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Custo estimado unitário</Label>
                    <Input
                      value={formData.custo_estimado_unitario}
                      onChange={(e) => updateField('custo_estimado_unitario', e.target.value)}
                      placeholder="R$ 0,00"
                    />
                  </div>
                  <div>
                    <Label>Preço alvo de venda</Label>
                    <Input
                      value={formData.preco_alvo_venda}
                      onChange={(e) => updateField('preco_alvo_venda', e.target.value)}
                      placeholder="R$ 0,00"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Margem bruta estimada</Label>
                    <Input
                      value={formData.margem_bruta_estimada}
                      onChange={(e) => updateField('margem_bruta_estimada', e.target.value)}
                      placeholder="Ex: 35%"
                    />
                  </div>
                  <div>
                    <Label>Investimento inicial necessário</Label>
                    <Input
                      value={formData.investimento_inicial}
                      onChange={(e) => updateField('investimento_inicial', e.target.value)}
                      placeholder="R$ 0,00"
                    />
                  </div>
                  <div>
                    <Label>Break-even estimado (unidades)</Label>
                    <Input
                      value={formData.break_even_unidades}
                      onChange={(e) => updateField('break_even_unidades', e.target.value)}
                      placeholder="Ex: 100"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 9 - Riscos */}
          <div>
            <SectionHeader 
              number="9" 
              title="RISCOS" 
              icon={AlertTriangle}
              expanded={expandedSections[9]}
              onToggle={() => toggleSection(9)}
            />
            {expandedSections[9] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Principais riscos técnicos</Label>
                  <Textarea
                    value={formData.riscos_tecnicos}
                    onChange={(e) => updateField('riscos_tecnicos', e.target.value)}
                    rows={2}
                    placeholder="Descreva os riscos técnicos..."
                  />
                </div>
                <div>
                  <Label>Principais riscos comerciais</Label>
                  <Textarea
                    value={formData.riscos_comerciais}
                    onChange={(e) => updateField('riscos_comerciais', e.target.value)}
                    rows={2}
                    placeholder="Descreva os riscos comerciais..."
                  />
                </div>
                <div>
                  <Label>Principais riscos operacionais</Label>
                  <Textarea
                    value={formData.riscos_operacionais}
                    onChange={(e) => updateField('riscos_operacionais', e.target.value)}
                    rows={2}
                    placeholder="Descreva os riscos operacionais..."
                  />
                </div>
                <div>
                  <Label>Nível de risco geral:</Label>
                  <RadioGroup 
                    value={formData.nivel_risco_geral} 
                    onValueChange={(v) => updateField('nivel_risco_geral', v)}
                    className="mt-2 flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="baixo" id="risco_baixo" />
                      <Label htmlFor="risco_baixo" className="font-normal">Baixo</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="medio" id="risco_medio" />
                      <Label htmlFor="risco_medio" className="font-normal">Médio</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="alto" id="risco_alto" />
                      <Label htmlFor="risco_alto" className="font-normal">Alto</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            )}
          </div>

          {/* Section 10 - Estratégia de Lançamento */}
          <div>
            <SectionHeader 
              number="10" 
              title="ESTRATÉGIA DE LANÇAMENTO" 
              icon={Rocket}
              expanded={expandedSections[10]}
              onToggle={() => toggleSection(10)}
            />
            {expandedSections[10] && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg space-y-4">
                <div>
                  <Label>Será lançado como:</Label>
                  <RadioGroup 
                    value={formData.tipo_lancamento} 
                    onValueChange={(v) => updateField('tipo_lancamento', v)}
                    className="mt-2 flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mvp" id="lanc_mvp" />
                      <Label htmlFor="lanc_mvp" className="font-normal">MVP</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="produto_final" id="lanc_final" />
                      <Label htmlFor="lanc_final" className="font-normal">Produto final completo</Label>
                    </div>
                  </RadioGroup>
                </div>
                <div>
                  <Label>Necessita piloto em campo?</Label>
                  <Input
                    value={formData.necessita_piloto}
                    onChange={(e) => updateField('necessita_piloto', e.target.value)}
                    placeholder="Sim/Não - detalhes..."
                  />
                </div>
                <div>
                  <Label>Cliente âncora definido?</Label>
                  <Input
                    value={formData.cliente_ancora}
                    onChange={(e) => updateField('cliente_ancora', e.target.value)}
                    placeholder="Sim/Não - qual cliente..."
                  />
                </div>
                <div>
                  <Label>Existe contrato ou apenas intenção?</Label>
                  <Input
                    value={formData.contrato_intencao}
                    onChange={(e) => updateField('contrato_intencao', e.target.value)}
                    placeholder="Contrato / Intenção / Nenhum"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-white pt-4 border-t">
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading || !formData.nome_projeto}>
                {loading ? "Criando..." : "Criar SDP"}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ========= REMAINING COMPONENTS (SDPDetailView and Modals) =========
// SDPDetailView, StatusChangeModal, ParecerModal, UploadModal, RevisionModal, ComponentModal
// These remain the same as before, keeping them for reference

export { CreateSDPModal, SectionHeader };
